/**
 * Exporting the scaffold on its own as binary glTF.
 *
 * Only the scaffold group is exported — never the terrain and never the
 * imported reference model. Because an imported model keeps the world
 * coordinates it was exported at, and nothing in the scaffold is ever shifted
 * to suit the viewport, the file lands back in the source model at the right
 * place.
 */

import type { Object3D } from "three";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import { fail, ok, type Result } from "../lib/result.ts";

export type GlbExportOptions = Readonly<{
  /**
   * Meshes to leave out — viewport affordances that are not real components.
   * They are hidden for the duration of the export and restored afterwards,
   * which the exporter honours through its `onlyVisible` default.
   */
  hide: readonly Object3D[];
}>;

function isArrayBuffer(value: unknown): value is ArrayBuffer {
  return value instanceof ArrayBuffer;
}

export async function toGlb(root: Object3D, options: GlbExportOptions): Promise<Result<ArrayBuffer>> {
  const restore: Object3D[] = [];
  for (const object of options.hide) {
    if (object.visible) {
      object.visible = false;
      restore.push(object);
    }
  }

  try {
    const exporter = new GLTFExporter();
    const result: unknown = await exporter.parseAsync(root, { binary: true, onlyVisible: true });

    if (!isArrayBuffer(result)) {
      return fail("The exporter did not return a binary glTF");
    }

    return ok(result);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);

    return fail(`Could not export the scaffold: ${msg}`, err);
  } finally {
    for (const object of restore) {
      object.visible = true;
    }
  }
}
